import { AppContext } from "App";

import { ISectionLogger } from "@multiweb/logging";
import { IResultMessage, IRunMethodResult, ITaskResultDetail } from "@multiweb/types/contracts";
import { isVersionOlderOrEqual } from "Global/Base/Utils/versionUtils";
import { IMEChangeStack, IMEConditionGroup, IMERecordsGroup, MEChangeStack } from "UI/UI.Controls/DataSource/MultiEdit";
import { ObjectContext } from "UI/UI/UI/UIManagerInterfaces";
import { DataConverter } from "../../Global/Base/Converters/DataConverter";
import { IEncryptedInfo, IEncryptedObjectDTO } from "../../Global/Base/Crypto/IEncryptedObject";
import { IEncryptionSettings } from "../../Global/Base/Crypto/IEncryptionSettings";
import { ISignatureDataDTO, ISignaturesDataDTO } from "../../Global/Base/Crypto/ISignatureDataDTO";
import { ICertificate, ISignatureInfo, ISignatureTemplate } from "../../Global/Base/Crypto/ISignatureInfo";
import { ISignatureProviderProfile } from "../../Global/Base/Crypto/ISignatureProvider";
import { CaseInsensitiveMap } from "../../Global/Base/DataTypes/CaseInsensitiveMap";
import { InvalidArgumentException, NotImplementedException } from "../../Global/Base/Exceptions";
import { GlobalContext } from "../../Global/Base/GlobalContext";
import { CancellationToken } from "../../Global/Base/Promises/CancellationTokenSource";
import { RightLevel } from "../../Global/Base/Rights/RightTypes";
import { ObjectUtils } from "../../Global/Base/Utils/ObjectUtils";
import { VersionInfo, versionInfo } from "../../Global/Base/VersionInfo";
import { EOWConditionException, EOWRightEvaluationException } from "../Basic/OWExceptions";
import { ASServices } from "../Basic/ServiceEndpoints";
import { ApplicationCache } from "./Cache/IAppCache";
import { IUserActiveRolesDTO } from "./Clients/impl/userSettings/UserSettingsClient";
import { convertResultMessages, IResultMessageDTO } from "./Clients/impl/utils";
import { ConditionStore } from "./ConditionStore";
import { EnumType } from "./DataTypes/Enums";
import { IConditionValidationResult, ILocalizedAttributeData } from "./DataTypes/IConditionValidation";
import { IOWObject } from "./DataTypes/IOWObject";
import { IOWObjectList } from "./DataTypes/IOWObjectList";
import { NullOWObject, OWObject } from "./DataTypes/OWObject";
import { OWObjectList } from "./DataTypes/OWObjectList";
import { ICommunicationManager } from "./ICommunicationManager";
import { InstanceManager } from "./InstanceManager";
import { IParticipant, IParticipantBase, IWorkflowMenuItem } from "./IWorkflowManager";
import { Localization } from "./Localization";
import { IClassInsertRightResult } from "./OWDescriptions";
import {
    ICreatedObjects,
    IDeleteObjectsResult,
    IDMSFileLimits,
    IExtendedObjectOperation,
    IImplicitFormInfo,
    IListFrameColumn,
    IMultiEditUpdatesResult,
    IOWObjectForTransfer,
    IOWObjectJsonPatchDTO,
    RunMethodForIdParams,
    RunMethodParams,
    RunObjectMethodParams,
    RunObjectMethodWithParamsParams,
    RunStaticMethodParams,
    RunStaticMethodWithParamsConfig,
} from "./OWManagerInterfaces";
import { getCancellationTokenAsRequestContext, RequestContext } from "./RequestContext";
import { RequestContextWithoutTransactionContextId } from "./RequestContextWithoutTransactionContextId";
import {
    ClassItemRightTypes,
    ClassItemType,
    IClassInterfaceManipulativeRoles,
    IClassItemRights,
    IEvaluatedConditionResult,
    IFirmlyDisabledRights,
    IRolesDefaultRightsResult,
} from "./Rights";
import { SerializeFuncs } from "./Serializers";

const _registeredConditions: Map<string, { conditionFunc: (obj: OWObject) => Promise<boolean>; isStatic: boolean }> = new Map();

/**
 * Object Wrapper Manager. Hlavní třída umožňující komunikaci s aplikačním serverem a provádění různých operací na jeho rozhraní.
 * @includeToDoc
 */
export class OWManager {
    private static _serverId: string | null = null;

    private static _dmsFileLimits: Promise<IDMSFileLimits>;
    private static _communicationManager: ICommunicationManager;
    private static _rightsLogger: ISectionLogger;
    private static _debugLocallyEvaluableConditions: boolean;
    private static _lastCallThatMayAffectRightsTime: number = 0;

    private static _updateLastCallThatMayAffectRightsTime(): void {
        this._lastCallThatMayAffectRightsTime = Date.now();
    }

    /**
     * Gets the time of the last call to server, that may affect rights evaluation results.
     */
    public static get lastCallThatMayAffectRightsTime(): number {
        return this._lastCallThatMayAffectRightsTime;
    }

    public static initialize(communicationManager: ICommunicationManager, rightsLogger: ISectionLogger): void {
        this._communicationManager = communicationManager;
        this._rightsLogger = rightsLogger;
        this._debugLocallyEvaluableConditions = !!AppContext.instance.configuration.getDevelopmentOption("debugLocallyEvaluableConditions");
    }

    /**
     * Najde název koncového atributu (např. A|B.C.x -> x)
     * @param attributeName Název atributu
     */
    public static getFinalAttribute(attributeName: string): string {
        if (!attributeName)
            return attributeName;

        const pos = attributeName.lastIndexOf(".");
        if (pos === -1)
            return attributeName;

        return attributeName.substr(pos + 1);
    }

    /**
     * Metoda požádá AS o vyhodnocení požadovaných práv na zadanou metodu
     * @param methodName název metody
     */
    public static staticMethodsRights(methodName: string, requestContext?: RequestContext): Promise<IClassItemRights> {
        return this._communicationManager.callGroupedPOSTRequest("StaticMethodsRights", ASServices.owManager, null, methodName, undefined, {
            resultIDParameterName: "Name",
            resultGroupedAttrName: "ItemRights",
        }, requestContext)
            .then((result) => {
                if (result.ExceptionText)
                    throw new EOWRightEvaluationException(methodName, result.ExceptionText);
                return result;
            });
    }

    /**
     * Pro zadanou třídu vrací informaci o tom, která práva (insert, edit, delete) jsou na serveru striktně zakázána,
     * Hodnota `true` značí, že právo je zakázané.
     * @param className Název třídy
     * @obsolete Use `FirmlyDisabledRights.get()` instead.
     */
    public static firmlyDisabledRights(className: string, requestContext?: RequestContext): Promise<IFirmlyDisabledRights> {
        return this._communicationManager.callGETRequest("FirmlyDisabledRights", ASServices.owManager, { className }, undefined, requestContext);
    }

    public static evaluateConditionWithoutObject(conditionID: string, requestContext?: RequestContext): Promise<boolean> {
        return this.evaluateCondition(null, conditionID, requestContext);
    }
    public static getObjectSignatureDefinition(instanceID: string, requestContext?: RequestContext): Promise<ISignatureTemplate> {
        return this._communicationManager.callGETRequest("ObjectSignatureDefinition", ASServices.cryptography, { instanceID: instanceID }, undefined, requestContext);
    }

    public static getObjectSignatureInfo(instanceID: string, requestContext?: RequestContext): Promise<ISignatureInfo | null> {
        if (!instanceID)
            return null;
        return this._communicationManager.callGroupedGETRequest("ObjectSignatureInfo", ASServices.cryptography, { instanceID: instanceID }, undefined, undefined, requestContext);
    }

    public static getCertificateInfo(cert: string, requestContext?: RequestContext): Promise<ICertificate> {
        return this._communicationManager.callPOSTRequest("CertificateInfo", ASServices.cryptography, null, cert, undefined, requestContext);
    }

    /**
     * Stáhne podpis dokumentu v textové formě, pokud je dostupný mimo dokument
     * @param instanceID Id instance aplikačního záznamu, na které je umístěný dokument
     * @param attributeName Název atributu aplikačního záznamu, ve kterém je umístěný dokument
     * @param cancellationToken Token umožňující zrušení dotazu
     */
    public static getDocumentSignature(instanceID: string, attributeName: string, requestContext?: RequestContext): Promise<string> {
        return this._communicationManager.callGroupedGETRequest("DocumentSignature", ASServices.cryptography, { instanceID: instanceID, attributeName: attributeName }, undefined, undefined, requestContext);
    }

    public static getDocumentSignatureInfo(instanceID: string, attributeName: string, requestContext?: RequestContext): Promise<ISignatureInfo> {
        return this._communicationManager.callGroupedGETRequest("DocumentSignatureInfo", ASServices.cryptography, { instanceID: instanceID, attributeName: attributeName }, undefined, undefined, requestContext);
    }

    public static getObjectSignatureData(instanceID: string, requestContext?: RequestContext): Promise<ISignatureDataDTO | null> {
        return this._communicationManager.callGETRequest("ObjectSignatureData", ASServices.cryptography, { instanceID: instanceID }, undefined, requestContext);
    }

    /**
     * Vrací nastavení šifrování.
     * Pokud je predano instanceID vrati nastaveni sifrovani pro dany objekt, jinak obecne nastaveni sifrovani
     */
    public static getEncryptionSettings(instanceID?: string, requestContext?: RequestContext): Promise<IEncryptionSettings> {
        return this._communicationManager.callGETRequest("GetEncryptionSettings", ASServices.cryptography, { instanceID: instanceID }, undefined, requestContext);
    }

    /**
     * Metoda pro uložení informací o zašifrovaných objektech
     * @param encObjsList Kolekce zašifrovaných objektů.
     * @param certificate Certifikát použitý k zašifrování (Base64).
     * @param algName Název algoritmu dle specifikace JWK.
     * @param requestContext Viz {@link RequestContext}
     */
    public static createEncryptionInfo(encObjsList: IEncryptedObjectDTO[], certificate: string, algName: string, requestContext?: RequestContext): Promise<void> {
        const encInfo: IEncryptedInfo = { algorithmName: algName, certificate: certificate, encObjsList: encObjsList };
        return this._communicationManager.callPOSTRequest("CreateEncryptionInfo", ASServices.cryptography, null, encInfo, undefined, requestContext);
    }

    /**
     * Získá JWK klíč pro asymetrické šifrování.
     * @param instanceID ID instance objektu.
     * @param encryptCertMethodName Metoda vracející veřejný klíč pro asymetrické zašifrování.
     * @param kty JWK key type.
     * @param algName Název JWK algoritmu.
     */
    public static getJsonWebKey(instanceID: string, encryptCertMethodName: string, kty: string, algName: string, requestContext?: RequestContext): Promise<JsonWebKey> {
        return this._communicationManager.callGETRequest("GetJsonWebKey", ASServices.cryptography, { instanceID: instanceID, encryptCertMethodName: encryptCertMethodName, kty: kty, algName: algName }, undefined, requestContext);
    }

    /**
     * Získá JWK klíč z certifikatu pro asymetrické šifrování.
     * @param certificate certifikat
     * @param keyType JWK key type.
     * @param algName Název JWK algoritmu.
     */
    public static getJsonWebKeyFromCertificate(certificate: string, keyType: string, algName: string, requestContext?: RequestContext): Promise<JsonWebKey> {
        return this._communicationManager.callPOSTRequest("GetJsonWebKeyFromCertificate", ASServices.cryptography, null, { Certificate: certificate, KeyType: keyType, AlgorithmName: algName }, undefined, requestContext);
    }

    /**
     * Uloží všechny specifikované aplikační záznamy v jedné transakci
     * @param identifiers Pole identifikátorů instancí aplikačních záznamů
     */
    public static applyUpdatesOnObjects(identifiers: string[], requestContext?: RequestContext): Promise<void> {
        return this._communicationManager.callPOSTRequest("ApplyUpdatesObjects", ASServices.owManager, {}, identifiers, undefined, requestContext)
            .then(() => this._updateLastCallThatMayAffectRightsTime());
    }

    /**
     * Aplikování hromadných změn
     * @param structure Struktura zmen
     * @param requestContext Viz {@link RequestContext}
     */
    public static applyMultiEditUpdates(structure: MEChangeStack, requestContext?: RequestContext): Promise<IMultiEditUpdatesResult> {
        return this._communicationManager.callPOSTRequest("ApplyUpdatesMultiEdit", ASServices.instances, undefined, {
            instances: structure.instances,
            groups: structure.groups.map(g => {
                return {
                    changes: g.changes,
                    excluded: g.excluded,
                    hasChanges: g.hasChanges,
                    condition: (g as IMEConditionGroup)?.condition,
                    conditionParams: (g as IMEConditionGroup)?.conditionParams,
                    records: (g as IMERecordsGroup)?.instances,
                } as unknown;
            }),
            className: structure.className,
            requiredAttrs: structure.requiredAttrs ? Array.from(structure.requiredAttrs, ([attrName, conditionId]) => ({ attrName, conditionId })) : [],
        } as IMEChangeStack, undefined, requestContext)
            .then(({ isInLongTransaction, messages }: IMultiEditUpdatesResultDTO) => ({
                isInLongTransaction,
                messages: convertResultMessages(messages),
            }))
            .then(result => {
                this._updateLastCallThatMayAffectRightsTime();
                return result;
            });
    }

    /**
     * Metoda pro hromadné založení objektů
     * @param objectName Název cílové třídy
     * @param masterObjectName Název zdrojové třídy
     * @param masterAttr Název vazby
     * @param masterId ID vazby
     * @param objectList Objekty, kterou budou založeny
     * @param attrName Název atributu vazební třídy, který obsahuje vazbu na instanci zdrojové třídy, ze které se vybírají záznamy
     * @param extendedOperations Parametry pro dodatečné nastavení objektu při vytváření
     * @param requestContext Viz {@link RequestContext}
     */
    public static createObjectsFromList(objectName: string, masterObjectName: string, masterAttr: string, masterId: string, objectList: OWObjectList, attrName: string, extendedOperations: IExtendedObjectOperation[],
        requestContext?: RequestContext): Promise<ICreatedObjects> {
        return this._communicationManager.callPOSTRequest("CreateObjectsFromList", ASServices.owManager,
            { objectName: objectName, masterObjectName: masterObjectName, masterAttr: masterAttr, masterId: masterId, attrName: attrName },
            { objectList: objectList, extendedOperations: extendedOperations }, undefined, requestContext)
            .then(({ objects, messages }: ICreatedObjectsDTO) => ({
                objects,
                messages: convertResultMessages(messages),
            }))
            .then(result => {
                this._updateLastCallThatMayAffectRightsTime();
                return result;
            });
    }

    /**
     * Deletes objects
     * @param formId Form, that called deletion
     * @param objectList Objects for deletion
     * @param condition A form condition to evaluate whether deletion is possible
     * @param requestContext See {@link RequestContext}
     * @param extendedOperations Array of extended operations for object creation before deletion
     */
    public static deleteObjects(formId: string, objectList: OWObjectList, condition?: string, requestContext?: RequestContext, extendedOperations?: IExtendedObjectOperation[]): Promise<IDeleteObjectsResult> {
        return this._communicationManager.callPOSTRequest("DeleteObjects", ASServices.owManager, {}, { formId: formId, objectList, condition, extendedOperations }, undefined, requestContext)
            .then(({ failedIds, inDispatcher, isInLongTransaction, resultMessages }: IDeleteObjectsResultDTO) => ({
                failedIds,
                inDispatcher,
                isInLongTransaction,
                messages: convertResultMessages(resultMessages),
            }))
            .then(result => {
                this._updateLastCallThatMayAffectRightsTime();
                return result;
            });
    }

    /**
     * Provede validaci podmínky
     * @param condition Podmínka k vyhodnocení
     * @param className Název třídy, nad kterou je podmínka vyhodnocována
     * @param names Lokalizované názvy atributů tak, jak je vidí uživatel
     * @param requestContext Viz {@link RequestContext}
     */
    public static async validateCondition(condition: string, className: string, names: ILocalizedAttributeData, requestContext?: RequestContext): Promise<IConditionValidationResult> {
        return this._communicationManager.callPOSTRequest("ValidateCondition", ASServices.owManager, { className: className }, { condition: condition, attrNames: names }, undefined, requestContext).catch(() => {
            return { isValid: false };
        });
    }

    /**
     * Znovu načte všechna data aplikačního záznamu z databáze a nahradí jimi data existující instance
     * @param instanceID Identifikátor instance, jejíž data se mají znovu načíst
     * @param withoutBigData Zda se má přeskočit načítání BigData atributů
     * @param requestContext Viz {@link RequestContext}
     */
    public static async reloadInstance(instanceID: string, withoutBigData: boolean = false, requestContext?: RequestContext): Promise<IOWObjectForTransfer> {
        return await this._communicationManager.callGETRequest("ReloadInstance", ASServices.instances, {
            instanceID: instanceID,
            withoutBigData: withoutBigData,
        }, undefined, requestContext);
    }

    public static async publishInstance(instanceID: string, attributeName: string, requestContext?: RequestContext): Promise<void> {
        const masterPublicInstance = InstanceManager.getInstance(instanceID);
        if (!masterPublicInstance)
            throw new Error(`Instance ${instanceID} is unavailable`);

        const publishedInstance = await masterPublicInstance.getValue(attributeName) as IOWObject;
        if (!publishedInstance || publishedInstance instanceof NullOWObject)
            return;

        const key = publishedInstance.instanceID;
        let cache: Map<string, Promise<void>> | null = null;
        if (ApplicationCache) {
            cache = OWManager._getPublishedInstancesCache();
            let waiter: Promise<void>;
            if (cache && (waiter = cache.get(key)))
                return waiter; // promise publikující instanci

            if (!cache) {
                cache = new Map<string, Promise<void>>();
                ApplicationCache.instance.setValue("publishedInstances", cache);
            }
        }

        const publishPromise = this._communicationManager.callPOSTRequest("PublishInstance", ASServices.owManager, { instanceID: instanceID, attributeName: attributeName }, undefined, undefined, requestContext);

        if (cache)
            cache.set(key, publishPromise);

        try {
            await publishPromise;
        } catch (e) {
            if (cache) // při chybě se smaže
                cache.delete(key);
        }
    }

    private static _getPublishedInstancesCache(): Map<string, Promise<void>> | null {
        if (!ApplicationCache)
            return null;
        const cache: Map<string, Promise<void>> = ApplicationCache.instance.getValue("publishedInstances") as Map<string, Promise<void>>;
        if (!cache)
            return null;
        return cache;
    }

    public static getObject(id: any, className: string, extendedOperations: IExtendedObjectOperation[] | null = null, withoutBigData: boolean = true, readVersioned: boolean = false, requestContext?: RequestContext): Promise<OWObject> {
        return this._communicationManager.callPOSTRequest("GetObject", ASServices.owManager, { className }, { id, extendedOperations, withoutBigData, readVersioned }, undefined, requestContext)
            .then((objectData: any) => OWObject.createObject(objectData, true, this._convertRequestToObjectContext(requestContext), undefined, requestContext?.cancellationToken));
    }

    public static newObject(className: string, extendedOperations: IExtendedObjectOperation[] = null, requestContext?: RequestContext): Promise<OWObject> {
        return this._communicationManager.callPOSTRequest("NewObject", ASServices.owManager, { className: className }, extendedOperations, undefined, requestContext)
            .then((objectData: any) => OWObject.createObject(objectData, true, OWManager._convertRequestToObjectContext(requestContext), undefined, requestContext?.cancellationToken));
    }

    public static getInsertRight(rightsClassName: string, requestContext?: RequestContext): Promise<boolean> {
        return this._communicationManager.callGETRequest("InsertRight", ASServices.owManager, { rightsClassName: rightsClassName }, undefined, requestContext);
    }

    public static getDeleteRight(rightsClassName: string, requestContext?: RequestContext): Promise<boolean> {
        return this._communicationManager.callGETRequest("DeleteRight", ASServices.owManager, { rightsClassName: rightsClassName }, undefined, requestContext);
    }

    public static async getAttributeLOVConditions(instanceID: string, className: string, attributeName: string, requestContext?: RequestContext): Promise<string> {
        await this.getUserRoleIDs(requestContext?.cancellationToken);
        return this._communicationManager.callGETRequest("AttributeLOVConditions", ASServices.owManager, { instanceID: instanceID, className: className, attributeName: attributeName }, undefined, requestContext);
    }

    public static getInsertRightWithValidationRights(rightsClassName: string, masterInstanceID: string = null, masterLinkToAttr: string = null, requestContext?: RequestContext): Promise<IClassInsertRightResult> {
        const params: { [paramName: string]: any } = {};
        if (rightsClassName != null)
            params["rightsClassName"] = rightsClassName;
        if (masterInstanceID != null) {
            params["masterInstanceID"] = masterInstanceID;
            if (masterLinkToAttr != null)
                params["masterLinkToAttr"] = masterLinkToAttr;
        }
        return this._communicationManager.callGroupedGETRequest("InsertRightWithValidation", ASServices.owManager, params, undefined, undefined, requestContext);
    }

    /**
     * Načte instanci aplikačního záznamu z paměti aplikačního serveru
     * @param instanceID Identifikátor instance, která se má načíst
     * @param withoutBigData Zda se má přeskočit načítání BigData atributů
     * @param afterChangeCycle
     * @param requestContext Viz {@link RequestContext}
     */
    public static getInstance(instanceID: string, withoutBigData: boolean = true, afterChangeCycle: number = -1, requestContext?: RequestContext): Promise<OWObject> {
        return this.getInstanceData(instanceID, withoutBigData, afterChangeCycle, requestContext)
            .then((objectData: any) => OWObject.createObject(objectData, true, OWManager._convertRequestToObjectContext(requestContext), undefined, requestContext?.cancellationToken));
    }

    private static _convertRequestToObjectContext(request?: RequestContext): ObjectContext {
        if (!request) {
            return { getTransactionContextId: () => undefined };
        } else {
            return {
                roles: request.roles,
                rightsSettingsVersion: request.rightsSettingsVersion,
                getTransactionContextId: request.getTransactionContextId,
            };
        }
    }

    public static getInstanceData(instanceID: string, withoutBigData: boolean = true, afterChangeCycle: number = -1, requestContext?: RequestContext): Promise<IOWObjectForTransfer> {
        return this._communicationManager.callGETRequest("GetInstance", ASServices.instances, { instanceID, withoutBigData, afterChangeCycle }, undefined, requestContext) as Promise<IOWObjectForTransfer>;
    }

    public static getInstanceImplicitForm(instanceID: string, attrName: string = "", conditionParameters: string = "", requestContext?: RequestContext): Promise<IImplicitFormInfo> {
        return this._communicationManager.callGETRequest("InstanceImplicitForm", ASServices.instances, { instanceID, attrName, conditionParameters }, undefined, requestContext);
    }

    public static getRecordImplicitForm(id: string, className: string, attrName: string = "", conditionParameters: string = "", requestContext?: RequestContext): Promise<IImplicitFormInfo> {
        return this._communicationManager.callGETRequest("RecordImplicitForm", ASServices.instances, { id, className, attrName, conditionParameters }, undefined, requestContext);
    }

    public static validateObject(instanceID: string, requestContext?: RequestContext): Promise<void> {
        return this._communicationManager.callGETRequest("Validate", ASServices.instances, { instanceID: instanceID }, undefined, requestContext);
    }

    /**
    * Vyhodnocuje vyplnění odloženě povinných atributů na třídě
    * @param instanceID Id instance aplikačního záznamu, na které je umístěný dokument
    * @param requestContext Viz {@link RequestContext}
    */
    public static postponedValidateObject(instanceID: string, requestContext?: RequestContext): Promise<void> {
        return this._communicationManager.callGETRequest("PostponedValidate", ASServices.instances, { instanceID: instanceID }, undefined, requestContext);
    }

    public static releaseInstances(instanceIDs: string[], requestContext?: RequestContextWithoutTransactionContextId): Promise<void> {
        return this._communicationManager.callPOSTRequest("ReleaseInstances", ASServices.owManager, null, instanceIDs, undefined, requestContext);
    }

    public static runStaticMethod({ className, methodName, userLocKey, formId, requestContext }: RunStaticMethodParams, _cancellationToken: CancellationToken = CancellationToken.none): Promise<IRunMethodResult> {
        return this._communicationManager.callGETRequest("Run", ASServices.owManager, { className: className, methodName: methodName, userLocKey: userLocKey, formId: formId }, undefined, requestContext)
            .then(transformRunMethodResult)
            .then(result => {
                this._updateLastCallThatMayAffectRightsTime();
                return result;
            });
    }

    public static runStaticMethodWithParams({ className, methodName, userLocKey, formId, params, requestContext }: RunStaticMethodWithParamsConfig, _cancellationToken: CancellationToken = CancellationToken.none): Promise<IRunMethodResult> {
        return this._communicationManager.callPOSTRequest("Run", ASServices.owManager, { className: className, methodName: methodName, userLocKey: userLocKey, formId: formId }, params == null ? [] : params, undefined, requestContext)
            .then(transformRunMethodResult)
            .then(result => {
                this._updateLastCallThatMayAffectRightsTime();
                return result;
            });
    }

    /**
     * Zavolá metodu nad více objekty
     * @param config Konfigurační objekt
     */
    public static runMethod({ formId, methodName, visibleCondId, enabledCondId, isVisibleCondNegative, isEnabledCondNegative, userLocKey, objectList, requestContext }: RunMethodParams, _cancellationToken: CancellationToken = CancellationToken.none): Promise<IRunMethodResult> {
        return this._communicationManager.callPOSTRequest("Run", ASServices.owManager,
            { formId: formId, methodName: methodName, visibleCondId: visibleCondId, enabledCondId: enabledCondId, userLocKey: userLocKey, isVisibleCondNegative: isVisibleCondNegative, isEnabledCondNegative: isEnabledCondNegative },
            { objectList: objectList }, undefined, requestContext).then(transformRunMethodResult)
            .then(result => {
                this._updateLastCallThatMayAffectRightsTime();
                return result;
            });
    }

    /**
     * Zavolá metodu objektu bez instance objektu
     * @param config Konfigurační objekt
     */
    public static runMethodForId({ id, className, methodName, userLocKey, formId, requestContext }: RunMethodForIdParams, _cancellationToken: CancellationToken = CancellationToken.none): Promise<IRunMethodResult> {
        return this._communicationManager.callGETRequest("Run", ASServices.instances,
            { id, className, methodName, userLocKey, formId }, undefined, requestContext).then(transformRunMethodResult)
            .then(result => {
                this._updateLastCallThatMayAffectRightsTime();
                return result;
            });
    }

    /**
     * Získá detailní informace o dokončeném úkolu.
     * @param resultId Kód záznamu
     * @param requestContext Viz {@link RequestContext}
     */
    public static longTaskResultDetail(resultId: string, requestContext?: RequestContext): Promise<ITaskResultDetail> {
        return this._communicationManager.callGETRequest("LongTaskResultDetail", ASServices.owManager, { resultId: resultId }, undefined, requestContext);
    }

    // Vrací seznam domén ze třídy CDoména
    public static getDomains(requestContext?: RequestContext): Promise<{ Name: string; Primary: boolean }[]> {
        return this._communicationManager.callGETRequest("Domains", ASServices.owManager, undefined, undefined, requestContext);
    }

    private static _enums: { [enumName: string]: Promise<EnumType> } = {};

    public static getEnum(enumName: string, cancellationToken: CancellationToken = CancellationToken.none): Promise<EnumType> {
        const r = OWManager._enums[enumName];
        if (r !== undefined)
            return r;

        return OWManager._enums[enumName] = this.getEnumDescriptions(enumName, cancellationToken).then<EnumType>((dscs) =>
            Localization.loadLocalizations(dscs.map((v) => v.LocKey)).then(() => new EnumType(dscs, enumName)),
        );
    }

    public static clearEnumCache(): void {
        this._enums = {};
    }

    private static _userRoleIDs: Promise<string[]> = null;

    public static getUserRoleIDs(cancellationToken: CancellationToken = CancellationToken.none): Promise<string[]> {
        if (OWManager._userRoleIDs === null) {
            OWManager._userRoleIDs = this._communicationManager.callGETRequest("UserRoleIDs", ASServices.owManager, undefined, undefined, getCancellationTokenAsRequestContext(cancellationToken));
        }

        return OWManager._userRoleIDs;
    }

    public static setRoleRanges(roleRangesDTO: IUserActiveRolesDTO[]): Promise<void> {
        return this._communicationManager.callPOSTRequest("SetRoleRanges", ASServices.owManager, undefined, roleRangesDTO);
    }

    public static getGlobalValue(name: string, cancellationToken: CancellationToken = CancellationToken.none): Promise<string> {
        return this._communicationManager.callGETRequest("GetGlobalValue", ASServices.owManager, { name: name }, undefined, getCancellationTokenAsRequestContext(cancellationToken));
    }

    public static setGlobalValue(name: string, value: string, requestContext?: RequestContext): Promise<void> {
        return this._communicationManager.callPOSTRequest("SetGlobalValue", ASServices.owManager, { name: name, value: value }, undefined, undefined, requestContext);
    }

    private static _isWFDefinedToClass: CaseInsensitiveMap<Promise<boolean>> = new CaseInsensitiveMap<Promise<boolean>>();
    /**
     * If there is Workflow defined for the specified class, returns true.
     *
     * @param className Name of the class to check Workflow for
     * @param requestContext Viz {@link RequestContext}
     * @param force Ignore cache
     * @return True when Workflow is defined for the specified class, false otherwise.
     */
    public static isWFDefinedToClass(className: string, requestContext?: RequestContext, force: boolean = false): Promise<boolean> {
        if (this._isWFDefinedToClass.has(className) && !force) return this._isWFDefinedToClass.get(className);
        const prm = this._communicationManager.callGETRequest("IsWFDefinedToClass", ASServices.descriptions, { className: className }, undefined, requestContext);
        this._isWFDefinedToClass.set(className, prm);

        return prm;
    }

    /**
     * Vrací seznam spouštěčů typu Menu pro daný objekt
     * Používá se pro konstrukci WF menu nad vybraným záznamem
     * @param instanceID - Identifikátor instance objektu
     * @param requestContext - Kontext, v rámci kterého bude požadavek vykonán. (Role uživatele, atd.)
     * @returns seznam workflowmenu objektů
     */
    public static objectWFMenu(instanceID: string, requestContext?: RequestContext): Promise<IWorkflowMenuItem[]> {
        return this._communicationManager.callGETRequest("ObjectWFMenu", ASServices.instances, { instanceID: instanceID }, undefined, requestContext);
    }

    /**
    * Vrací seznam spouštěčů typu Menu pro seznam objektů
    * Používá se pro konstrukci WF menu při hromadné editaci: průnik spouštěčů za objekty
    * @param objectList - Seznam objektů, pro které se má hledat průnik spouštěčů
    * @param requestContext - Kontext, v rámci kterého bude požadavek vykonán. (Role uživatele, atd.)
    * @returns seznam workflowmenu objektů
    */
    public static objectsWFMenu(objectList: IOWObjectList, requestContext?: RequestContext): Promise<IWorkflowMenuItem[]> {
        return this._communicationManager.callPOSTRequest("ObjectsWFMenu", ASServices.owManager, undefined, objectList, undefined, requestContext);
    }

    /**
    * Vrací seznam hráčů nad vybranou aktivitou WorkFlow
    * @param instanceID identifikátor instance objektu
    * @param objectList seznam objektů, nahrazuje instanceID v případě multieditace, jinak může být null
    * @param activityID identifikátor aktivity
    * @param requestContext Kontext, v rámci kterého bude požadavek vykonán. (Role uživatele, atd.)
    * @returns seznam objektů typu Hráč (identifikátor, název)
    */
    public static getActivityParticipants(instanceID: string, objectList: IOWObjectList, activityID: string, requestContext?: RequestContext): Promise<IParticipant[]> {
        if (objectList) {
            return this._communicationManager.callPOSTRequest("ActivityParticipants", ASServices.owManager, { activityID: activityID }, objectList, undefined, requestContext);
        } else {
            return this._communicationManager.callGETRequest("ActivityParticipants", ASServices.instances, { instanceID: instanceID, activityID: activityID }, undefined, requestContext);
        }
    }

    /**
     * Provede akce WorkFlow nad seznamem objektů
     * @param ids identifikátory objektů
     * @param className třída objektů
     * @param actionID identifikátor spouštěče
     * @param nextParticipants
     * @param requestContext Viz {@link RequestContext}
     */
    public static objectWFActionExecuteWithoutInstance(ids: string[], className: string, actionID: string, nextParticipants: IParticipant[], requestContext?: RequestContext): Promise<IRunMethodResult> {
        if (!ids || ids.length !== 1) {
            //TODO dodělat v rámci #30151
            throw new NotImplementedException();
        }
        return this._communicationManager.callPOSTRequest("ObjectWFActionExecute", ASServices.instances, { id: ids[0], className, actionID }, { nextParticipants: nextParticipants }, undefined, requestContext)
            .then(transformRunMethodResult)
            .then(result => {
                this._updateLastCallThatMayAffectRightsTime();
                return result;
            });
    }

    /**
    * Provede akce WorkFlow navázané na spouštěč Menu
    * @param instanceID - identifikátor instance objektu
    * @param actionID - identifikátor spouštěče
    * @param nextParticipants
    * @param paramFormObjectIID - identifikátor instance neperzistentního objektu vytvořeného parametrickým formulářem
    * @param requestContext - Kontext, v rámci kterého bude daná akce vykonána. (Role uživatele, atd.)
    */
    public static objectWFActionExecute(instanceID: string, actionID: string, nextParticipants: IParticipantBase[], paramFormObjectIID?: string, requestContext?: RequestContext): Promise<IRunMethodResult> {
        return this._communicationManager.callPOSTRequest("ObjectWFActionExecute", ASServices.instances, { instanceID: instanceID, actionID: actionID, paramFormObjectIID: paramFormObjectIID }, nextParticipants, undefined, requestContext)
            .then(transformRunMethodResult)
            .then(result => {
                this._updateLastCallThatMayAffectRightsTime();
                return result;
            });
    }

    public static objectListWFActionExecute(objectList: OWObjectList, actionID: string, nextParticipants: IParticipantBase[], paramFormObjectIID?: string, requestContext?: RequestContext): Promise<IRunMethodResult> {
        return this._communicationManager.callPOSTRequest("ObjectListWFActionExecute", ASServices.instances, { actionID: actionID, paramFormObjectIID: paramFormObjectIID }, {
            objectList: SerializeFuncs.serializeOWObjectList(objectList),
            nextParticipants: nextParticipants,
        }, undefined, requestContext)
            .then(transformRunMethodResult)
            .then(result => {
                this._updateLastCallThatMayAffectRightsTime();
                return result;
            });
    }

    // #region DMS
    /**
     * Získá url pro stažení souboru z DMS
     * @param instanceId Identifikátor instance objektu
     * @param attributeName Název atributu
     * @param version Verze dokumentu
     * @param requestContext Viz {@link RequestContext}
     */
    public static getDocumentDownloadUrl(instanceId: string, attributeName: string, version: string = "", requestContext?: RequestContext): Promise<string | null> {
        return this._communicationManager.callGETRequest("DMSDownloadURL", ASServices.dms, { instanceID: instanceId, attributeName: attributeName, version: version }, undefined, requestContext)
            .then(url => OWManager._urlToLowerCase(url)); // potřeba pro jednotné nastavení CSP
    }

    /**
     * Získá url pro stažení souboru z DMS
     * @param objectId Jednoznačný identifikátor objektu.
     * @param objectName Název třídy objektu
     * @param attributeName Název atributu, který obsahuje odkaz na dokument uložený v DMS
     * @param requestContext Viz {@link RequestContext}
     */
    public static getDocumentDownloadUrlByObjectId(objectId: string, objectName: string, attributeName: string, requestContext?: RequestContext): Promise<string | null> {
        return this._communicationManager.callGETRequest("DMSDownloadURL", ASServices.dms, { id: objectId, objectName, attributeName }, undefined, requestContext)
            .then(url => OWManager._urlToLowerCase(url)); // potřeba pro jednotné nastavení CSP
    }

    /**
     * Vygeneruje url pro stažení souboru přes PublicDocumentGetter (odkaz má být trvalý a před stažením má být ověřena dostupnost pro daného uživatele)
     * Protože se jedná o trvalý odkaz, nebudeme podporovat editContextId v url.
     * @param className Třída, kde leží atribut s daným souborem
     * @param id Záznam, ze kterého chceme stáhnout soubor
     * @param attributeName Požadovaný atribut
     * @param archiveVersion Zda má být stažena verze dokumentu pro archivování
     * @param version ID archivní verze dokumentu, která má být stažena
     */
    public static getPublicDocumentDownloadUrl(className: string, id: string, attributeName: string, version?: string): string;
    public static getPublicDocumentDownloadUrl(className: string, id: string, attributeName: string, version: string): string;
    public static getPublicDocumentDownloadUrl(className: string, id: string, attributeName: string, archiveVersion: boolean): string;
    public static getPublicDocumentDownloadUrl(className: string, id: string, attributeName: string, version: boolean | string = false): string {
        const params = `${className};${id};${attributeName};${version}`;
        const handlerUrlPart = isVersionOlderOrEqual(AppContext.instance.configuration.getDevelopmentOption("webServiceVersion"), "15.03") ? "handlers/document.aspx" : "api/v01/as/exports/document";
        return new URL(`${handlerUrlPart}?docID=${DataConverter.btoaUtf8(params)}`, `${AppContext.instance.addresses.serviceUrl}/`).href;
    }

    /**
     * Získá url pro hromadné stažení souborů z DMS v rámci jednoho archivu
     * @param fileName Název výsledného archivu
     * @param objList ObjectList se záznamy, ze kterých se mají stahovat dokumenty
     * @param attrNames Názvy atributů třídy, ze kterých se mají stahovat dokumenty
     * @param requestContext Viz {@link RequestContext}
     */
    public static getDocumentsDownloadUrl(fileName: string, objList: OWObjectList, attrNames: string[], requestContext?: RequestContext): Promise<string | null> {
        return this._communicationManager.callPOSTRequest("DMSDownloadURL", ASServices.dms, { fileName: fileName }, { objects: objList, attributeNames: attrNames }, undefined, requestContext)
            .then(url => OWManager._urlToLowerCase(url)); // potřeba pro jednotné nastavení CSP
    }

    /**
     * Zjišťuje url pro upload do DMS
     * @param fileExtension Přípona souboru včetně tečky na začátku
     * @param size Velikost souboru k nahrání v bytech
     * @param instanceId Identifikátor instance objektu, kterému bude dokument přiřazen (nutné u šifrovaných dokumentů)
     * @param attributeName Název atributu, ve kterém bude odkaz na dokument (nutné u šifrovaných dokumentů)
     * @param requestContext Viz {@link RequestContext}
     */
    public static getDocumentUploadUrl(fileExtension: string, size: number, instanceId?: string, attributeName?: string, requestContext?: RequestContext): Promise<string | null> {
        return this._communicationManager.callPOSTRequest("DMSUploadURL", ASServices.dms, null, { fileExtension: fileExtension, size: size, instanceId: instanceId, attributeName: attributeName }, undefined, requestContext)
            .then(url => OWManager._urlToLowerCase(url)); // potřeba pro jednotné nastavení CSP
    }

    /**
     * Vrátí URL na avatara pro daného uživatele
     * @param userId ID uživatele
     * @param avatarType Typ avatara
     * @param requestContext Viz {@link RequestContext}
     */
    public static getAvatarUrl(userId: number, avatarType: "original" | string = "original", requestContext?: RequestContext): Promise<string | null> {
        return this._communicationManager.callGETRequest("DMSAvatarURL", ASServices.dms, { userId, avatarType }, undefined, requestContext)
            .then(url => OWManager._urlToLowerCase(url)); // potřeba pro jednotné nastavení CSP
    }

    /**
     * Získá limity pro nahrání dat do DMS. Povolené přípony a jejich povolené velikosti.
     * @param requestContext Viz {@link RequestContext}
     */
    public static async getDMSFileLimits(requestContext?: RequestContext): Promise<IDMSFileLimits> {
        if (!OWManager._dmsFileLimits) {
            OWManager._dmsFileLimits = this._communicationManager.callGETRequest("DMSFileLimits", ASServices.dms, undefined, undefined, requestContext).then(dmsFileLimitsRaw => ObjectUtils.transformKeysToLowerCamelCase(dmsFileLimitsRaw) as IDMSFileLimits);
        }

        return OWManager._dmsFileLimits;
    }
    // #endregion DMS

    private static _urlToLowerCase(url: string): string | null {
        if (!url || typeof url !== "string")
            return null;

        const endOfUrl = url.indexOf("?"); // atributy by se neměly převádět na lower case
        return endOfUrl === -1 ? url.toLowerCase() : url.substring(0, endOfUrl).toLowerCase() + url.substring(endOfUrl);
    }

    /**
     * registrace funkce reprezentující podmínku
     * @param conditionID identifikátor podmínky
     * @param conditionFunc funkce reprezentující podmínku
     * @param isStatic jestli je funkce neobsahuje atributy a je vyhodnotitelná bez instance objektu
     * @param desc textová reprezentace podmínky
     */
    public static registerCondition(conditionID: string, conditionFunc: (obj: OWObject) => Promise<boolean>, isStatic: boolean): void {
        _registeredConditions.set(conditionID, { conditionFunc: conditionFunc, isStatic: isStatic });
    }

    /**
     * Načte profil s nastavením pro podepisování konkrétního objektu nebo dokumentu
     * @param instanceID identifikátor záznamu
     * @param attributeName název atributu, ve kterém je dokument, nebo null pro objekt
     * @param fileExtension nepovinná přípona podepisovaného dokumentu
     * @param requestContext Viz {@link RequestContext}
     */
    public static getDigitalSignatureSettings(instanceID: string, attributeName: string | null, fileExtension: string | null = null,
        requestContext?: RequestContext): Promise<ISignatureProviderProfile | null> {
        return this._communicationManager.callGETRequest("DigitalSignatureSettings", ASServices.cryptography, { instanceID: instanceID, attributeName: attributeName || undefined, fileExtension: fileExtension || undefined, forNewSignature: true }, undefined, requestContext);
    }

    /**
     * Unikátní identifikátor aplikačního serveru
     * @param cancellationToken Token k přerušení operace
     */
    public static async getServerId(cancellationToken: CancellationToken = CancellationToken.none): Promise<string> {
        if (OWManager._serverId == null)
            OWManager._serverId = await OWManager.getGlobalValue("ApplicationID", cancellationToken) || "localhost";

        return OWManager._serverId;
    }

    /**
     * Validuje předaný digitální podpis a připojuje jej k dokumentu
     * @param instanceID identifikátor záznamu
     * @param attributeName Název atributu, který obsahuje odkaz na dokument
     * @param signature Digitální podpis vytvořený na klientské straně nebo null, pokud je podpis v objektu
     * @param profileId ID profilu s nastavením provideru
     * @param providerType Typ podpisovacího provideru
     * @param providerVersion Verze podpisovacího provideru
     * @param requestContext Viz {@link RequestContext}
     * @returns Výsledek validace a připojení digitálního podpisu k dokumentu
     */
    public static createDocumentSignature(instanceID: string, attributeName: string, signature: string, profileId: string, providerType: string, providerVersion: string,
        requestContext?: RequestContext): Promise<string> {
        if (!instanceID)
            throw new InvalidArgumentException("instanceID");
        if (!attributeName)
            throw new InvalidArgumentException("attributeName");
        if (!providerType)
            throw new InvalidArgumentException("providerType");
        if (!providerVersion)
            throw new InvalidArgumentException("providerVersion");

        return this._communicationManager.callPOSTRequest("CreateDocumentSignature", ASServices.cryptography, { instanceID: instanceID, attributeName: attributeName },
            { signature: signature, profileId: profileId, providerType: providerType, providerVersion: providerVersion }, undefined, requestContext);
    }

    /**
    * Validuje předaný digitální podpis a připojuje jej k aplikačnímu záznamu
    * @param signatures Kolekce podpisů, které se mají uložit a jejich provider
    * @param signatureSourcesEncrypted Příznak, jestli zdrojová data digitálního podpisu jsou zašifrovaná.
    * @param requestContext Viz {@link RequestContext}
    * @returns Výsledek validace a připojení digitálního podpisu k dokumentu nebo objektu
    */
    public static createObjectsSignature(signatures: ISignaturesDataDTO[], signatureSourcesEncrypted: boolean = false, requestContext?: RequestContext): Promise<string> {
        return this._communicationManager.callPOSTRequest("CreateSignatures", ASServices.cryptography, { signatureSourcesEncrypted: signatureSourcesEncrypted }, signatures, undefined, requestContext);
    }

    /**
     * Zpracuje návratovou hodnotu obdrženou při uložení podpisu na službě. Pokud byl podpis uložen vrací true. V opačném případě vrací ještě lokalizační klíč chybové hlášky.
     * @param result Text vrácený při uložení podpisu na službu.
     */
    public static parseSigningResult(result: string): [boolean, number] {
        let boolResult: boolean = true;
        let locKey: number = 0;
        switch (result) {
            case "Signed": // podepsáno
            case "UnknownSignature": // neznámý
            case "AllowUnknownSignature": // neznámý, umožníme vložit //AllowUnknownSignature i UnknownSignature jsou v podstatě stejné. Nově chceme povolit uložení v obou případech.
            case "Encrypted": // šifrován - nevíme, zda tam podpis je nebo...
            case "SignedAndEncrypted": // podepsáno a šifrováno
                break;
            case "Empty": // prázdný
                locKey = 665636; //Záznam nelze podepsat. Podpis nebyl vytvořen.
                boolResult = false;
                break;
            case "Error":
            default:
                locKey = 389142; //Při ověření certifikátu došlo k neočekávané chybě. Záznam nelze podepsat. Kontaktujte správce systému.
                boolResult = false;
                break;
        }
        return [boolResult, locKey];
    }

    /**
     * Získá metodou vytvořený seznam sloupců, které mají být přidány do seznamu
     * @param methodName - metoda, která vygeneruje sloupce
     * @param instanceID - instance mastera
     * @param requestContext Viz {@link RequestContext}
     */
    public static async getListFrameColumns(methodName: string, instanceID: string, requestContext?: RequestContext): Promise<IListFrameColumn[]> {
        return this._communicationManager.callGETRequest("GetListFrameColumns", ASServices.owManager, { methodName: methodName, instanceID: instanceID }, undefined, requestContext);
    }

    /**
     * Registrace naslouchání obecnému DataEventu
     * @example OWManagerCommunication.subscribeDataEvent("ExchangeRates", "RateValue", "USD", function(msg){ logger.debug("USD: " + msg.Message.Body) }); OWManagerCommunication.subscribeDataEvent("ExchangeRates", "RateValue", "*", function(msg){ logger.debug(msg.Message.SubType + ": " + msg.Message.Body) });
     * @param source Pojmenování zdroje zpráv (pro systémové zprávy je to "OW", pro ostatní podle jejich nastavení)
     * @param type Typ zprávy
     * @param subType Volitelný podtyp zprávy - může zpřesňovat naslouchání jen menší množině zpráv ("*" značí naslouchání všemu)
     * @param onMessage Listener volaný při obdržení zprávy
     */
    /*public static subscribeDataEvent(source: string, type: string, subType: string, onmessage: (message: any) => void): void {
        ComunicationWorkerConnector.subscribeDataEvent(source, type, subType, onmessage);

    }*/

    /**
     * Odregistrování naslouchání obecnému DataEventu
     * @example OWManagerCommunication.unsubscribeDataEvent("ExchangeRates", "RateValue", "USD");
     * @param source Pojmenování zdroje zpráv (pro systémové zprávy je to "OW", pro ostatní podle jejich nastavení)
     * @param type Typ zprávy
     * @param subType Volitelný podtyp zprávy - může zpřesňovat naslouchání jen menší množině zpráv ("*" značí naslouchání všemu)
     * @param onMessage Listener volaný při obdržení zprávy
     */
    /*public static unsubscribeDataEvent(source: string, type: string, subType: string, onmessage?: (message: any) => void): void {
        ComunicationWorkerConnector.unsubscribeDataEvent(source, type, subType, onmessage);
    }*/

    /**
     * Vyhodnotí práva na položky
     * @param instanceID Instance objektu
     * @param name Název atributu
     * @param type Typ (atribut nebo metoda)
     * @param evalRights Práva, které se mají vyhodnotit.
     * @param requestContext Viz {@link RequestContext}
     */
    public static objectItemsRights(instanceID: string, name: string, type: ClassItemType, evalRights: ClassItemRightTypes, requestContext?: RequestContext): Promise<IClassItemRights & { ExceptionText: string }> {
        return this._communicationManager.callGroupedPOSTRequest("ItemsRights", ASServices.instances, { instanceID: instanceID }, { Name: name, Type: type, EvalRights: evalRights }, undefined, {
            resultIDParameterName: "Name",
            resultGroupedAttrName: "ItemRights",
            groupByBodyAttr: "Name",
        }, requestContext);
    }

    /**
     * Vyhodnotí statická práva na položky (bez vybraného objektu)
     * @param className Název třídy
     * @param name Název atributu
     * @param type Typ (atribut nebo metoda)
     * @param evalRights Práva, které se mají vyhodnotit.
     * @param requestContext Viz {@link RequestContext}
     */
    public static classItemsRights(className: string, name: string, type: ClassItemType, evalRights: ClassItemRightTypes, requestContext?: RequestContext): Promise<IClassItemRights & { ExceptionText: string }> {
        return this._communicationManager.callGroupedPOSTRequest("ClassItemsRights", ASServices.owManager, { className }, { Name: name, Type: type, EvalRights: evalRights }, undefined, {
            resultIDParameterName: "Name",
            resultGroupedAttrName: "ItemRights",
            groupByBodyAttr: "Name",
        }, requestContext);
    }

    /**
     * Načte defaultní práva prvků objektu práv
     * @param objectName Objekt práv (formulář, třída, systémová operace)
     * @param level Úroveň práv
     * @param roleids Seznam rolí, ke kterým mají být zjištěna defaultní práva
     * @param requestContext Viz {@link RequestContext}
     */
    public static getAllRolesDefaultRights(objectName: string, level: RightLevel, roleids: readonly string[], requestContext?: RequestContext): Promise<IRolesDefaultRightsResult> {
        return this._communicationManager.callPOSTRequest("GetRoleDefaultRights", ASServices.owManager, { objectName }, { Level: level, RoleIDs: roleids }, undefined, requestContext);
    }

    /**
     * Pro zadanou třídu vrací informaci o tom, která práva (insert, edit, delete) jsou na serveru striktně zakázána,
     * Hodnota `true` značí, že právo je zakázané.
     * @param className Název třídy
     */
    public static classInterfaceManipulativeRoles(className: string, requestContext?: RequestContext): Promise<IClassInterfaceManipulativeRoles> {
        return this._communicationManager.callGETRequest("ClassInterfaceManipulativeRoles", ASServices.owManager, { className }, undefined, requestContext);
    }

    /**
     * Odeslání dotazu na vyhodnocení podmínky na server. Dotazy se seskupují
     * @param instanceID Instance, nad kterou se má podmínka vyhodnocovat
     * @param conditionID Identifikátor podmínky
     * @param requestContext Viz {@link RequestContext}
     * @returns
     */
    private static _evaluateConditionRequest(instanceID: string | null, conditionID: string, requestContext?: RequestContext): Promise<boolean> {
        return this._communicationManager.callGroupedPOSTRequest("EvaluateConditions", ASServices.instances, { instanceID: instanceID ? instanceID : "-1" }, conditionID, ConditionStore.isLocalEvaluable(conditionID) ? 100 : undefined, {
            resultIDParameterName: "ID",
        }, requestContext).then((result: IEvaluatedConditionResult) => {
            if (result.ExceptionText)
                //OWManager._rightsLogger.error(`Condition Error: (${conditionID})  ${result.ExceptionText}`);
                throw new EOWConditionException(conditionID, result.ExceptionText);
            return result.Result;
        });
    }

    /**
     * Vnitřní metoda pro vyhodnocení podmínky. Parametrem lze ovlivnit způsob zpracování lokálně vyhodnotitelných podmínek.
     * @param obj Objekt, nad kterým se má podmínka vyhodnotit
     * @param conditionID Identifikátor podmínky
     * @param debugLocallyEvaluableConditions Příznak, zda se mají i lokálně vyhodnotitelné podmínky posílat k vyhodnocení i na server
     * @param requestContext Viz {@link RequestContext}
     * @returns Vrací výsledek vyhodnocení podmínky
     */
    private static async _evaluateCondition(obj: OWObject | null, conditionID: string, debugLocallyEvaluableConditions: boolean = false, requestContext?: RequestContext): Promise<boolean | null> {
        let localResult: boolean | null = null;
        let locallyFailed = false;
        try {
            const condItem = _registeredConditions.get(conditionID + "");
            if (condItem) {
                if (!obj && !condItem.isStatic)
                    throw new EOWConditionException(conditionID, `Condition: ${conditionID} is not evaluable without object instance!`); //`

                localResult = await condItem.conditionFunc(obj);
            } else {
                locallyFailed = true; // Pokud není podmínka lokálně vyhodnotitelná, musí se vyhodnotit na serveru
            }
        } catch (ex) {
            if (ex instanceof EOWConditionException)
                throw ex;

            locallyFailed = true;
            OWManager._rightsLogger.warn(`Error in local evaluate condition ${conditionID}. Will try to evaluate remotely.`, ex); //`
        }

        /**
         * Pokud se podařilo podmínku lokálně vyhodnotit a je vypnuto ladící srovnávací vyhodnocování lokálně vyhodnotitelných
         * podmínek na serveru, pak vracíme výsledek. Jinak pokračujeme vyhodnocením na serveru.
         */
        if (!debugLocallyEvaluableConditions && !locallyFailed) {
            return localResult;
        }

        const serverResult = await this._evaluateConditionRequest(obj ? obj.instanceID : null, conditionID, requestContext);

        if (debugLocallyEvaluableConditions) {
            const resultsDiffer = serverResult !== localResult;

            /**
             * Pokud se podařilo podmínku vyhodnotit lokálně a liší se od serverového výsledku,
             * zapíšeme o tom chybovou hlášku.
             */
            if (localResult !== null && resultsDiffer) {
                OWManager._rightsLogger.error(`Incorrectly locally evaluated condition ${conditionID} ${obj ? `on instance ${obj.instanceID}` : "(static)"}! Server result will be used. Local result: "${localResult}" Server result: "${serverResult}" Condition: "${ConditionStore.getCondition(conditionID.toString())}"`); //`
            }

            /**
             * Pokud se nepodařilo lokálně vyhodnotit nebo se lokální a serverový výsledek liší,
             * pak se použije výsledek serverového vyhodnocení.
             */
            return resultsDiffer ? serverResult : localResult;
        } else {
            return serverResult;
        }
    }

    /**
     * Vyhodnotí podmínku. Pokud je to možné, pak se defaultně lokálně vyhodnotitelné podmínky neposílají k vyhodnocení na server (pokud nedojde lokálně k chybě).
     * Toto defaultní chování lze změnit v development módu příznakem `debugLocallyEvaluableConditions`.
     * @param obj Objekt, nad kterým se má podmínka vyhodnotit
     * @param conditionID Identifikátor podmínky
     * @param requestContext Viz {@link RequestContext}
     * @returns Vrací výsledek vyhodnocení podmínky
     */
    public static async evaluateCondition(obj: OWObject | null, conditionID: string, requestContext?: RequestContext): Promise<boolean | null> {
        try {
            return await this._evaluateCondition(obj, conditionID, this._debugLocallyEvaluableConditions, requestContext);
        } catch (err) {
            OWManager._rightsLogger.warn(`Error in evaluate condition ${conditionID}:`, err.toString()); //`
            return null;
        }
    }

    // Vrací popis hodnot výčtových typů aplikační logiky
    public static getEnumDescriptions(enumName: string, cancellationToken: CancellationToken = CancellationToken.none): Promise<{ Description: string; IntValue: number; Name: string; LocKey: string }[]> {
        return this._communicationManager.callGroupedPOSTRequest("Enums", ASServices.descriptions, null, enumName, 100, { resultIDParameterName: "EnumName" }, getCancellationTokenAsRequestContext(cancellationToken)).then((v: any) => v.Items);
    }

    // Spuštění procedury objektu aplikační logiky
    public static runObjectMethod({ instanceID, methodName, userLocKey, formId, requestContext }: RunObjectMethodParams): Promise<IRunMethodResult> {
        return this._communicationManager.callGETRequest("Run", ASServices.instances, { instanceID: instanceID, methodName: methodName, userLocKey: userLocKey, formId: formId }, undefined, requestContext)
            .then(transformRunMethodResult)
            .then(result => {
                this._updateLastCallThatMayAffectRightsTime();
                return result;
            });
    }

    // Spuštění procedury objektu aplikační logiky
    public static runObjectMethodWithParams({ instanceID, methodName, userLocKey, formId, requestContext, params }: RunObjectMethodWithParamsParams, _cancellationToken: CancellationToken = CancellationToken.none): Promise<IRunMethodResult> {
        return this._communicationManager.callPOSTRequest("Run", ASServices.instances, { instanceID: instanceID, methodName: methodName, userLocKey: userLocKey, formId: formId }, params, undefined, requestContext)
            .then(transformRunMethodResult)
            .then(result => {
                this._updateLastCallThatMayAffectRightsTime();
                return result;
            });
    }

    // Uloží objekt do DB
    public static objectApplyUpdates(instanceID: string, requestContext?: RequestContext): Promise<{ actualizedObjectData: any; messages: IResultMessage[] }> {
        return this._communicationManager.callGETRequest("ApplyUpdates", ASServices.instances, { instanceID: instanceID }, undefined, requestContext)
            .then(({ actualizedObjectData, messages }) => ({ actualizedObjectData, messages: convertResultMessages(messages) }))
            .then(result => {
                this._updateLastCallThatMayAffectRightsTime();
                return result;
            });
    }

    // Smaže záznam v DB
    public static objectDelete(instanceID: string, requestContext?: RequestContext): Promise<IResultMessage[]> {
        return this._communicationManager.callGETRequest(/*'DeleteObject'*/"Delete", ASServices.instances, { instanceID: instanceID }, undefined, requestContext)
            .then(convertResultMessages)
            .then(result => {
                this._updateLastCallThatMayAffectRightsTime();
                return result;
            });
    }

    /** Změní hodnoty zadaných atributů a vrátí algoritmikou změněné atributy (včetně zadaných). Slouží i pro šifrované atributy.
     * @param instanceID Identifikátor objektu, na kterém se mají atributy nastavit
     * @param patchData Pole dvojic názvu atributu a jeho (ne)šifrované hodnoty
     * @param withoutBigData Zda se má přeskočit načítání BigData atributů
     * @param requestContext Viz {@link RequestContext}
     */
    public static async patchObject(instanceID: string, patchData: IOWObjectJsonPatchDTO[], withoutBigData: boolean = true, requestContext?: RequestContext): Promise<any> {
        const headers = {
            "Content-Type": "application/vnd.tescosw.ow-object.json-patch+json; charset=utf-8",
            "Accept": "application/vnd.tescosw.ow-object+json, */*; q=0.01",
            "X-TSC-withBigData": !withoutBigData,
        };
        return this._communicationManager.callPATCHRequest(instanceID, ASServices.data, undefined, patchData, headers, undefined, requestContext)
            .then(result => {
                this._updateLastCallThatMayAffectRightsTime();
                return result;
            });
    }

    // Vrací instanci objektu odpovídající odkazu ve vazebním atributu
    public static getAttributeObject(instanceID: string, attributeName: string, initFunc?: (obj: OWObject) => void, requestContext?: RequestContext): Promise<OWObject> {
        return this._communicationManager.callGETRequest("LOVAttribute", ASServices.instances, { attributeName: attributeName, instanceID: instanceID }, -100, requestContext)
            .then((objectData: any) => OWObject.createObject(objectData, false, OWManager._convertRequestToObjectContext(requestContext), initFunc, requestContext?.cancellationToken));
    }

    // Vrací hodnoty atributů
    public static getAttributesValues(instanceID: string, attributeNames: string[], withoutBigData: boolean = true, requestContext?: RequestContext): Promise<IOWObjectForTransfer> {
        return this._communicationManager.callPOSTRequest("AttributesValues", ASServices.instances, { instanceID: instanceID, withoutBigData: withoutBigData }, attributeNames, -100, requestContext);
    }

    // Vytvoří kopii objektu
    public static createCopyObject(instanceID: string, deleteOriginalInstance: boolean = false, requestContext?: RequestContext): Promise<OWObject> {
        return this._communicationManager.callGETRequest("CreateCopy", ASServices.instances, { instanceID: instanceID, deleteOriginalObjectInstance: deleteOriginalInstance }, undefined, requestContext)
            .then((objectData: any) => OWObject.createObject(objectData, true, OWManager._convertRequestToObjectContext(requestContext), undefined, requestContext?.cancellationToken));
    }

    // Vrací serverový datum a čas
    public static getSysDateTime(requestContext?: RequestContext): Promise<string> {
        return this._communicationManager.callGETRequest("SysDateTime", ASServices.owManager, undefined, undefined, requestContext);
    }

    // Vrací informace o verzi
    public static async getVersionInfo(requestContext?: RequestContext): Promise<VersionInfo> {
        // předpokládá se, že se nezmění verze bez restartu
        if (!versionInfo.server.versionId)
            versionInfo.server.versionId = await this._communicationManager.callGETRequest("VersionID", ASServices.owManager, undefined, undefined, requestContext);
        return versionInfo;
    }
}

const transformRunMethodResult = (result: IRunMethodResultDTO): IRunMethodResult => ({
    resultValue: result.ResultValue,
    isInLongTransaction: result.IsInLongTransaction,
    messages: convertResultMessages(result.Messages),
});

interface IRunMethodResultDTO {
    IsInLongTransaction: boolean;
    Messages: IResultMessageDTO[];
    ResultValue: any;
}

interface IMultiEditUpdatesResultDTO {
    isInLongTransaction: boolean;
    messages: IResultMessageDTO[];
}

interface ICreatedObjectsDTO {
    objects: string[];
    messages: IResultMessageDTO[];
}

interface IDeleteObjectsResultDTO {
    isInLongTransaction: boolean;
    inDispatcher: boolean;
    failedIds: string[];
    resultMessages: IResultMessageDTO[];
}

// TODO: Po rozjeti modulu upravit aplikaci (Je to tu kvuli registerCondition na definici formulare) a tohle odstranit
GlobalContext.register("OW", { Remoting: { OWManager: OWManager } });
GlobalContext.registerType(OWManager);
